import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { showBulkActionToast } from "@/lib/bulkActionToast";
import { formatDate } from "@/lib/formatDate";
import useHighlightParam from "@/hooks/useHighlightParam";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileDown, Mail, Loader2, FileText, Send, FileSignature, Trash2, ServerCog, Banknote, Sparkles, AlertTriangle, FileCode2, Search, X } from "lucide-react";
import { StatusFilterPills, useStatusPills } from "@/components/StatusFilterPills";
import { CreditLight, CreditWarning, isEuCountry } from "@/components/customers/CreditLight";
import { PaymentTermsSuggestion } from "@/components/invoices/PaymentTermsSuggestion";
import { useNavigate } from "react-router-dom";
import { VatRateDateHint } from "@/components/vat/VatRateDateHint";
import { QuickInvoiceDialog } from "@/components/invoices/QuickInvoiceDialog";
import { EmptyState } from "@/components/EmptyState";
import { Checkbox } from "@/components/ui/checkbox";
import { LsvCollectionWizard } from "@/components/LsvCollectionWizard";
import { BatchDetailModal } from "@/components/BatchDetailModal";

const VAT_RATES = [
  { label: "0% (steuerfrei)", value: 0 },
  { label: "8.1% (Normalsatz, ab 2024)", value: 0.081 },
  { label: "2.6% (reduziert, ab 2024)", value: 0.026 },
  { label: "3.8% (Beherbergung, ab 2024)", value: 0.038 },
  { label: "7.7% (Normalsatz bis 2023)", value: 0.077 },
  { label: "2.5% (reduziert bis 2023)", value: 0.025 },
  { label: "3.7% (Beherbergung bis 2023)", value: 0.037 },
];

export function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const invFilter = useStatusPills("invoices.statusFilter", localStorage.getItem("demoCompanyId"));
  const INV_STATUS = [["all","Alle"],["open","Offen"],["draft","Entwurf"],["sent","Gesendet"],["partially_paid","Teilbezahlt"],["overdue","Überfällig"],["paid","Bezahlt"]];
  const OPEN_STATUSES = ["sent", "partially_paid", "overdue"];
  const isOpenInv = (i) => OPEN_STATUSES.includes(i.status);
  const openInvoices = invoices.filter(isOpenInv);
  const openSum = openInvoices.reduce((s, i) => s + (Number(i.total) || 0) - (Number(i.amount_paid) || 0), 0);
  const statusCount = (key) => (key === "all" ? invoices.length : key === "open" ? openInvoices.length : invoices.filter((i) => i.status === key).length);
  const overdueDays = (i) => {
    if (!isOpenInv(i) || !i.due_date) return 0;
    const d = Math.floor((Date.now() - new Date(i.due_date).getTime()) / 86400000);
    return d > 0 ? d : 0;
  };
  const overdueCount = openInvoices.filter((i) => overdueDays(i) > 0).length;
  const sendDunning = async (e, inv, level) => {
    e.stopPropagation();
    if (!window.confirm(`Mahnung${level ? ` Stufe ${level}` : ""} für ${inv.invoice_number} jetzt erstellen und per E-Mail senden?`)) return;
    try {
      const r = await apiClient.post(`/invoices/${inv._id}/send-dunning`, { send_email: true });
      toast.success(`Mahnung Stufe ${r.data.level ?? r.data.dunning_level ?? ""} erstellt${r.data.email_sent ? " und gesendet" : ""}`);
      loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Mahnung fehlgeschlagen"); }
  };
  const [invSearch, setInvSearch] = useState("");
  const invMatches = (i, q) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const hay = [i.invoice_number, i.customer_name, i.qr_reference, i.issue_date, i.issue_date ? new Date(i.issue_date).toLocaleDateString("de-CH") : "", i.status, i.paid_via]
      .filter(Boolean).join(" ").toLowerCase();
    if (hay.includes(s)) return true;
    const num = parseFloat(s.replace(/'/g, "").replace(",", "."));
    return !Number.isNaN(num) && i.total != null && (Math.abs(Number(i.total) - num) < 0.005 || Number(i.total).toFixed(2).startsWith(s.replace(/'/g, "").replace(",", ".")));
  };
  const visibleInvoices = (invFilter.value === "all" ? invoices : invFilter.value === "open" ? openInvoices : invoices.filter((i) => i.status === invFilter.value)).filter((i) => invMatches(i, invSearch));
  const [dunWarn, setDunWarn] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [creditScores, setCreditScores] = useState({});
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [lsvWizardOpen, setLsvWizardOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailBatchId, setDetailBatchId] = useState(null);
  const [paymentIdemKey, setPaymentIdemKey] = useState(() => crypto.randomUUID());
  const [paymentForm, setPaymentForm] = useState({
    payment_date: "",
    amount: "",
    bank_account_id: "1020",
    description: "",
  });
  const EMPTY_FORM = {
    customer_id: "",
    payment_terms_note: "",
    invoice_number: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    currency: "CHF",
    vat_rate: 0.081,
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [ebillEnabled, setEbillEnabled] = useState(false);
  const [ebillSendingId, setEbillSendingId] = useState(null);
  const [mftpfEnabled, setMftpfEnabled] = useState(false);
  const [mftpfSendingId, setMftpfSendingId] = useState(null);
  const [mftpfSyncBusy, setMftpfSyncBusy] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");

  const calcTotals = () => {
    const subtotal = form.line_items.reduce(
      (sum, li) => sum + li.quantity * li.unit_price,
      0,
    );
    const tax = subtotal * form.vat_rate;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const loadInvoices = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/invoices`);
      setInvoices(res.data || []);
      apiClient.get(`/companies/${companyId}/dunning-warnings`).then((w) => setDunWarn(w.data)).catch(() => setDunWarn(null));
      apiClient.get(`/companies/${companyId}/contacts`, { params: { type: "customer" } }).then((r) => setCustomers(r.data || [])).catch(() => {});
      apiClient.get(`/companies/${companyId}/customers/credit-scores`).then((r) => setCreditScores(r.data?.scores || {})).catch(() => {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);


  // Iter101: Bulk-Aktionen für Rechnungen
  const [bulkBusy, setBulkBusy] = useState(false);
  const runBulkDeleteInvoices = async () => {
    if (selectedInvoiceIds.length === 0) return;
    if (
      !window.confirm(
        `${selectedInvoiceIds.length} Rechnung(en) löschen? Verbuchte werden übersprungen.`
      )
    )
      return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/invoices/bulk-delete`,
        { invoice_ids: selectedInvoiceIds }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} gelöscht${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadInvoices,
      });
      setSelectedInvoiceIds([]);
      loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Löschung fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };
  const runBulkMarkSent = async () => {
    if (selectedInvoiceIds.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/invoices/bulk-status`,
        { invoice_ids: selectedInvoiceIds, status: "sent" }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} als 'gesendet' markiert${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadInvoices,
      });
      setSelectedInvoiceIds([]);
      loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Update fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };

  useHighlightParam(invoices, (i) => i._id || i.id);

  useEffect(() => {
    if (!companyId) return;
    const loadCompanyDefaults = async () => {
      try {
        const res = await apiClient.get(`/companies/${companyId}`);
        const rate = res.data?.default_vat_rate;
        if (rate !== null && rate !== undefined) {
          setForm((prev) => ({
            ...prev,
            vat_rate: Number(rate) / 100,
          }));
        }
        setEbillEnabled(!!res.data?.ebill_enabled);
        // Iter109: MFTPF-Enabled-State separat laden (live aus settings)
        try {
          const mr = await apiClient.get(`/companies/${companyId}/mftpf-settings`);
          setMftpfEnabled(!!mr.data?.enabled);
        } catch (_) { /* silent */ }
      } catch (e) {
        console.error(e);
      }
    };
    loadCompanyDefaults();
  }, [companyId]);

  const handleLineChange = (index, field, value) => {
    const items = [...form.line_items];
    items[index] = {
      ...items[index],
      [field]: field === "description" ? value : Number(value),
    };
    setForm({ ...form, line_items: items });
  };

  const addLineItem = () => {
    setForm({
      ...form,
      line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }],
    });
  };

  const customerById = Object.fromEntries(customers.map((c) => [c._id, c]));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.customer_id) { toast.error("Bitte einen Kunden wählen"); return; }
    const { subtotal, tax, total } = calcTotals();

    const payload = {
      customer_id: form.customer_id,
      invoice_number: form.invoice_number,
      issue_date: form.issue_date,
      due_date: form.due_date || null,
      payment_terms_note: form.payment_terms_note || null,
      currency: form.currency,
      line_items: form.line_items,
      subtotal,
      tax_amount: tax,
      total,
      status: "draft",
    };

    try {
      await apiClient.post(`/companies/${companyId}/invoices`, payload);
      setOpenDialog(false);
      setForm(EMPTY_FORM);
      loadInvoices();
    } catch (e) {
      console.error(e);
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-emerald-100 text-emerald-800" data-testid="invoice-status-badge-paid">Bezahlt</Badge>;
      case "sent":
        return <Badge className="bg-sky-100 text-sky-800" data-testid="invoice-status-badge-sent">Gesendet</Badge>;
      case "partially_paid":
        return <Badge className="bg-amber-100 text-amber-800" data-testid="invoice-status-badge-partially-paid">Teilbezahlt</Badge>;
      case "overdue":
        return <Badge className="bg-rose-100 text-rose-800" data-testid="invoice-status-badge-overdue">Überfällig</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700" data-testid="invoice-status-badge-draft">Entwurf</Badge>;
    }
  };

  const { subtotal, tax, total } = calcTotals();

  const handlePostInvoice = async (invoice) => {
    try {
      await apiClient.post(`/invoices/${invoice._id}/post`, {});
      loadInvoices();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteInvoice = async (invoice) => {
    const isPosted = invoice.status && invoice.status !== "draft";
    const msg = isPosted
      ? `Rechnung "${invoice.invoice_number}" ist verbucht (Status: ${invoice.status}).\n\nLöschen entfernt auch alle verknüpften Buchungen (Verbuchung + Zahlungen). Fortfahren?`
      : `Rechnung "${invoice.invoice_number}" wirklich löschen?`;
    if (!window.confirm(msg)) return;
    try {
      const url = isPosted
        ? `/invoices/${invoice._id}?force=true`
        : `/invoices/${invoice._id}`;
      const res = await apiClient.delete(url);
      const dj = res.data?.deleted_journal_entries || 0;
      toast.success(
        dj > 0
          ? `Rechnung gelöscht (${dj} Buchung/en mit entfernt).`
          : "Rechnung gelöscht."
      );
      loadInvoices();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Löschen fehlgeschlagen.";
      toast.error(String(detail));
    }
  };

  const openPaymentForInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    const d = new Date();
    setPaymentForm({
      payment_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      amount: String(Math.max((invoice.total ?? 0) - (invoice.amount_paid ?? 0), 0).toFixed(2)),
      bank_account_id: "1020",
      description: "",
    });
    setPaymentIdemKey(crypto.randomUUID());
    setOpenPaymentDialog(true);
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    try {
      await apiClient.post(`/invoices/${selectedInvoice._id}/payments`, {
        payment_date: paymentForm.payment_date,
        amount: Number(paymentForm.amount),
        bank_account_id: paymentForm.bank_account_id,
        description: paymentForm.description,
      }, { headers: { "Idempotency-Key": paymentIdemKey } });
      setOpenPaymentDialog(false);
      setSelectedInvoice(null);
      toast.success("Zahlung erfasst");
      loadInvoices();
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Zahlung konnte nicht erfasst werden");
    }
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownloadPdf = async (invoice, facturx = false) => {
    setDownloadingId(invoice._id);
    try {
      const res = await apiClient.get(`/invoices/${invoice._id}/${facturx ? "facturx-pdf" : "pdf"}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rechnung_${invoice.invoice_number || invoice._id}${facturx ? "_FacturX" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(facturx ? "Factur-X PDF (mit E-Rechnungs-XML) heruntergeladen" : "PDF heruntergeladen");
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || "PDF-Generierung fehlgeschlagen";
      toast.error(String(detail));
    } finally {
      setDownloadingId(null);
    }
  };

  const [emailDialog, setEmailDialog] = useState({ open: false, invoice: null });
  const [emailForm, setEmailForm] = useState({ recipient_email: "", subject: "", body: "" });
  const [emailSending, setEmailSending] = useState(false);

  const openEmailFor = (invoice) => {
    setEmailForm({ recipient_email: "", subject: "", body: "" });
    setEmailDialog({ open: true, invoice });
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailDialog.invoice) return;
    setEmailSending(true);
    try {
      const res = await apiClient.post(
        `/invoices/${emailDialog.invoice._id}/send-email`,
        {
          recipient_email: emailForm.recipient_email || null,
          subject: emailForm.subject || null,
          body: emailForm.body || null,
        }
      );
      toast.success(`Rechnung gesendet an ${res.data.recipient}`);
      setEmailDialog({ open: false, invoice: null });
      loadInvoices();
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || "E-Mail-Versand fehlgeschlagen";
      toast.error(String(detail));
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendEbill = async (invoice) => {
    if (!companyId || !invoice?._id) return;
    setEbillSendingId(invoice._id);
    try {
      const res = await apiClient.post(
        `/companies/${companyId}/invoices/${invoice._id}/ebill/send`,
      );
      if (res.data?.mocked) {
        toast.success(
          `eBill (Test-Modus): ${res.data.invoice_number || invoice.invoice_number} im Delivery-Log eingetragen.`,
        );
      } else if (res.data?.success) {
        toast.success(
          `An eBill gesendet (HTTP ${res.data.status_code}).`,
        );
      } else {
        toast.error(`eBill-Versand fehlgeschlagen: ${res.data?.error || "unbekannt"}`);
      }
      loadInvoices();
    } catch (e) {
      const detail = e.response?.data?.detail || "eBill-Versand fehlgeschlagen";
      toast.error(String(detail));
    } finally {
      setEbillSendingId(null);
    }
  };

  // Iter109: One-Click pain.001 via PostFinance MFTPF
  const handleSendMftpf = async (invoice) => {
    if (!companyId || !invoice?._id) return;
    if (!window.confirm(`pain.001 für Rechnung ${invoice.invoice_number} jetzt via MFTPF an PostFinance senden?`)) return;
    setMftpfSendingId(invoice._id);
    try {
      const res = await apiClient.post(
        `/companies/${companyId}/invoices/${invoice._id}/mftpf/upload-pain001`,
      );
      const d = res.data || {};
      const modeBadge = d.mode === "mock" ? "Mock" : "Live";
      toast.success(`✅ ${modeBadge}: ${d.filename} (${d.bytes} Bytes) hochgeladen.`);
      loadInvoices();
    } catch (e) {
      const detail = e.response?.data?.detail || "MFTPF-Upload fehlgeschlagen";
      toast.error(String(detail));
    } finally {
      setMftpfSendingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {dunWarn && (dunWarn.due_count > 0 || dunWarn.repeat_customer_count > 0) && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="dunning-warning-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {dunWarn.due_count > 0 && <span data-testid="dunning-warning-due">{dunWarn.due_count} Mahnung{dunWarn.due_count === 1 ? "" : "en"} fällig (offen CHF {Number(dunWarn.due_total).toLocaleString("de-CH", { minimumFractionDigits: 2 })})</span>}
            {dunWarn.due_count > 0 && dunWarn.repeat_customer_count > 0 && " · "}
            {dunWarn.repeat_customer_count > 0 && <span data-testid="dunning-warning-repeat">{dunWarn.repeat_customer_count} Kunde{dunWarn.repeat_customer_count === 1 ? "" : "n"} in den letzten 12 Monaten mehrfach gemahnt</span>}
          </span>
          {dunWarn.due_count > 0 && <Button size="sm" variant="outline" className="h-7 border-amber-300 bg-white" onClick={() => navigate("/dunning")} data-testid="dunning-warning-go">Zum Mahnwesen</Button>}
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Rechnungen
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Erstellen, verbuchen und Zahlungen erfassen.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="rounded-full border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 px-4"
          onClick={() => setQuickOpen(true)}
          title="Freitext eingeben – die KI erstellt, bucht und versendet die Rechnung"
          data-testid="quick-invoice-button"
        >
          <Sparkles className="h-4 w-4 mr-1.5" /> Schnellrechnung
        </Button>
        <QuickInvoiceDialog open={quickOpen} onOpenChange={setQuickOpen} companyId={companyId} onCreated={loadInvoices} />
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-invoice-button"
              className="rounded-full bg-slate-900 text-slate-50 px-5"
            >
              Rechnung erstellen
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-invoice-dialog">
            <DialogHeader>
              <DialogTitle>Neue Rechnung</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              onSubmit={handleCreate}
              data-testid="add-invoice-form"
            >
              <div className="space-y-1">
                <Label htmlFor="invoice_customer">Kunde</Label>
                <Select value={form.customer_id} onValueChange={(value) => setForm({ ...form, customer_id: value, payment_terms_note: "", due_date: form.payment_terms_note ? "" : form.due_date })}>
                  <SelectTrigger id="invoice_customer" data-testid="invoice-customer-select">
                    <SelectValue placeholder={customers.length ? "Kunde wählen" : "Noch keine Kunden – zuerst unter Kunden anlegen"} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c._id} value={c._id} data-testid={`invoice-customer-option-${c._id}`}>
                        <span className="inline-flex items-center gap-2"><CreditLight score={creditScores[c._id]} compact />{c.name}{c.city ? ` · ${c.city}` : ""}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <CreditWarning score={creditScores[form.customer_id]} customerName={customerById[form.customer_id]?.name} testId="invoice-form-credit-warning" />
              <PaymentTermsSuggestion
                companyId={companyId}
                customerId={form.customer_id}
                issueDate={form.issue_date}
                dueDate={form.due_date}
                note={form.payment_terms_note}
                onApply={(patch) => setForm((f) => ({ ...f, ...patch }))}
                onClearNote={() => setForm((f) => ({ ...f, payment_terms_note: "", due_date: "" }))}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="invoice_number">Rechnungsnummer</Label>
                  <Input
                    id="invoice_number"
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    required
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="currency">Währung</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(value) => setForm({ ...form, currency: value })}
                  >
                    <SelectTrigger
                      id="currency"
                      data-testid="invoice-currency-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CHF">CHF</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="issue_date">Rechnungsdatum</Label>
                  <SwissDateField
                    id="issue_date"
                    
                    value={form.issue_date}
                    onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                    required
                    data-testid="invoice-issue-date-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="due_date">Fällig am <span className="text-slate-400 font-normal">(leer = Zahlungsfrist Kunde/Firma)</span></Label>
                  <SwissDateField
                    id="due_date"
                    
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="vat-rate">MWST-Satz</Label>
                  <Select
                    value={String(form.vat_rate)}
                    onValueChange={(value) => setForm({ ...form, vat_rate: Number(value) })}
                  >
                    <SelectTrigger
                      id="vat-rate"
                      data-testid="invoice-vat-rate-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VAT_RATES.map((rate) => (
                        <SelectItem
                          key={rate.value}
                          value={String(rate.value)}
                          data-testid={`invoice-vat-rate-option-${rate.value}`}
                        >
                          {rate.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <VatRateDateHint companyId={companyId} date={form.issue_date} ratePercent={Math.round(form.vat_rate * 1000) / 10} onApply={(r) => setForm({ ...form, vat_rate: r / 100 })} />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Positionen</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    data-testid="add-invoice-line-button"
                  >
                    Position hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.line_items.map((li, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-5 gap-2 items-center"
                      data-testid={`invoice-line-row-${idx}`}
                    >
                      <Input
                        placeholder="Beschreibung"
                        value={li.description}
                        onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                        className="col-span-3"
                        data-testid={`invoice-line-description-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={li.quantity}
                        onChange={(e) => handleLineChange(idx, "quantity", e.target.value)}
                        data-testid={`invoice-line-quantity-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={li.unit_price}
                        onChange={(e) => handleLineChange(idx, "unit_price", e.target.value)}
                        data-testid={`invoice-line-unit-price-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 mt-3 text-sm text-slate-700 space-y-1">
                <div className="flex justify-between">
                  <span>Zwischensumme (Netto)</span>
                  <span data-testid="invoice-subtotal">{subtotal.toFixed(2)} {form.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span>MWST ({(form.vat_rate * 100).toFixed(1)}%)</span>
                  <span data-testid="invoice-tax">{tax.toFixed(2)} {form.currency}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total (Brutto)</span>
                  <span data-testid="invoice-total">{total.toFixed(2)} {form.currency}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setOpenDialog(false); setForm(EMPTY_FORM); }}
                  data-testid="add-invoice-cancel-button"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  className="rounded-full bg-slate-900 text-slate-50 px-5"
                  data-testid="add-invoice-submit-button"
                >
                  Speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card
        className="p-4 border border-slate-200 bg-white shadow-sm"
        data-testid="invoices-table-card"
      >
        {selectedInvoiceIds.length > 0 && (() => {
          const selected = invoices.filter((i) => selectedInvoiceIds.includes(i._id));
          const totalSel = selected.reduce((acc, i) => acc + (parseFloat(i.total) || 0), 0);
          const lsvCount = selected.filter(
            (i) => ["sent", "overdue"].includes(i.status) && !i.lsv_collection_batch_id
          ).length;
          const draftCount = selected.filter((i) => i.status === "draft").length;
          return (
            <div
              className="-m-4 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-t-md border-b border-emerald-200 bg-emerald-50 px-4 py-2.5"
              data-testid="invoices-bulk-action-bar"
            >
              <div className="text-sm text-emerald-900">
                <span className="font-semibold">{selectedInvoiceIds.length}</span> ausgewählt ·{" "}
                <span className="font-semibold">{totalSel.toFixed(2)} CHF</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setSelectedInvoiceIds([])}
                  data-testid="invoices-clear-selection-button"
                >
                  Auswahl aufheben
                </Button>
                {draftCount > 0 && (
                  <Button
                    variant="outline" size="sm"
                    onClick={runBulkMarkSent}
                    disabled={bulkBusy}
                    data-testid="invoices-bulk-mark-sent"
                    title="Markiert die ausgewählten Entwürfe als verschickt, ohne sie erneut zu senden (z.B. bei Postversand)"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Als gesendet markieren ({draftCount})
                  </Button>
                )}
                <Button
                  variant="outline" size="sm"
                  onClick={runBulkDeleteInvoices}
                  disabled={bulkBusy}
                  className="text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="invoices-bulk-delete"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Löschen
                </Button>
                {lsvCount > 0 && (
                  <Button
                    size="sm"
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                    onClick={() => setLsvWizardOpen(true)}
                    data-testid="invoices-lsv-collect-button"
                    title="Betrag bei Kunden mit Lastschrift-Ermächtigung (LSV) direkt vom Konto einziehen"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Lastschrift einziehen ({lsvCount})
                  </Button>
                )}
              </div>
            </div>
          );
        })()}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">
            Alle Rechnungen
          </h2>
          <div className="flex items-center gap-2">
            {mftpfEnabled && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={mftpfSyncBusy}
                onClick={async () => {
                  if (!window.confirm(
                    "PostFinance camt.054 jetzt synchronisieren?\n\n" +
                    "Eingehende Kundenzahlungen (mit QR-Referenz) werden automatisch den offenen Rechnungen zugeordnet und als bezahlt markiert.",
                  )) return;
                  setMftpfSyncBusy(true);
                  try {
                    const r = await apiClient.post(`/companies/${companyId}/mftpf/sync-camt054`);
                    const d = r.data;
                    if (d.files_processed === 0) {
                      toast.info("Keine camt.054-Dateien gefunden");
                    } else {
                      toast.success(
                        `${d.files_processed} camt.054 verarbeitet · ${d.invoices_marked_paid} Rechnung(en) gezahlt · ${d.unmatched_count} ungemacht`,
                      );
                      await loadInvoices();
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "camt.054-Sync fehlgeschlagen");
                  } finally {
                    setMftpfSyncBusy(false);
                  }
                }}
                className="border-sky-300 text-sky-700 hover:bg-sky-50"
                data-testid="invoices-mftpf-sync-camt054-btn"
                title="Eingehende Kundenzahlungen über PostFinance MFTPF (camt.054) synchronisieren"
              >
                <Banknote className="h-3.5 w-3.5 mr-1" />
                {mftpfSyncBusy ? "Synchronisiere…" : "Kundenzahlungen synchronisieren"}
              </Button>
            )}
            {loading && (
              <span
                className="text-xs text-slate-400"
                data-testid="invoices-loading-indicator"
              >
                Lade ...
              </span>
            )}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <StatusFilterPills testidPrefix="invoices" value={invFilter.value} onChange={invFilter.setValue} def={invFilter.def}
            options={INV_STATUS.filter(([k]) => ["all", "open", "paid"].includes(k) || invoices.some((i) => i.status === k) || k === invFilter.value).map(([key, label]) => ({ key, label, count: statusCount(key) }))} />
          <span className="text-xs text-slate-600" data-testid="invoices-open-summary">
            <b className="text-slate-900">{openInvoices.length} offen</b> · CHF {openSum.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ausstehend
            {overdueCount > 0 && <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-800" data-testid="invoices-overdue-count">{overdueCount} überfällig</span>}
          </span>
          <div className="relative ml-auto w-full sm:w-80">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input type="text" value={invSearch} onChange={(e) => setInvSearch(e.target.value)}
              placeholder="Suchen: Kunde, Nr., Betrag, Datum, QR-Referenz …"
              className="w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 py-1.5 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
              data-testid="invoices-search" />
            {invSearch && (
              <button type="button" onClick={() => setInvSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Suche löschen" data-testid="invoices-search-clear"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
          {invSearch && <div className="w-full text-[11px] text-slate-500" data-testid="invoices-search-count">{visibleInvoices.length} Treffer für «{invSearch}»</div>}
        </div>
        <Table data-testid="invoices-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    invoices.length > 0 &&
                    invoices.every((i) => selectedInvoiceIds.includes(i._id))
                  }
                  onCheckedChange={() => {
                    const ids = invoices.map((i) => i._id);
                    const allSelected = ids.every((id) => selectedInvoiceIds.includes(id));
                    setSelectedInvoiceIds(allSelected ? [] : ids);
                  }}
                  data-testid="invoices-select-all-checkbox"
                  aria-label="Alle auswählen"
                />
              </TableHead>
              <TableHead>Datum</TableHead>
              <TableHead>Rechnung</TableHead>
              <TableHead>Kunde</TableHead>
              <TableHead>Betrag</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow data-testid="invoices-empty-row">
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    testid="invoices-empty-state"
                    icon={<FileText className="h-5 w-5" />}
                    title="Noch keine Rechnungen"
                    description="Erstellen Sie Ihre erste Rechnung — inklusive Swiss QR-Einzahlungsschein und PDF-Versand."
                  />
                </TableCell>
              </TableRow>
            )}
            {invoices.length > 0 && visibleInvoices.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center text-sm text-slate-500 py-6" data-testid="invoices-filter-empty">Keine Rechnungen mit diesem Status.</TableCell></TableRow>
            )}
            {visibleInvoices.map((inv) => {
              const eligibleForLsv =
                ["sent", "overdue"].includes(inv.status) && !inv.lsv_collection_batch_id;
              return (
              <TableRow
                key={inv._id}
                data-testid={`invoice-row-${inv._id}`}
                data-highlight-id={inv._id}
                data-overdue={overdueDays(inv) > 0 ? "true" : "false"}
                className={selectedInvoiceIds.includes(inv._id) ? "bg-emerald-50/40" : overdueDays(inv) > 0 ? "bg-rose-50/60" : ""}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedInvoiceIds.includes(inv._id)}
                    onCheckedChange={() =>
                      setSelectedInvoiceIds((prev) =>
                        prev.includes(inv._id)
                          ? prev.filter((x) => x !== inv._id)
                          : [...prev, inv._id],
                      )
                    }
                    aria-label={`Rechnung ${inv.invoice_number} auswählen${eligibleForLsv ? " (LSV-fähig)" : ""}`}
                    data-testid={`invoice-select-checkbox-${inv._id}`}
                  />
                </TableCell>
                <TableCell>{formatDate(inv.issue_date)}</TableCell>
                <TableCell>
                  <div>{inv.invoice_number}</div>
                  {inv.qr_reference && (
                    <div
                      className="text-[10px] text-slate-400 font-mono"
                      data-testid={`invoice-qr-ref-${inv._id}`}
                      title="QR-Referenz für den Bankabgleich"
                    >
                      QR: {String(inv.qr_reference).replace(/(.{5})/g, "$1 ").trim()}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-slate-900 inline-flex items-center gap-1.5"><CreditLight score={creditScores[inv.customer_id]} compact testId={`invoice-credit-${inv._id}`} />{inv.customer_name || "—"}</div>
                  {!inv.customer_name && (
                    <div className="text-[10px] text-slate-400 font-mono">{inv.customer_id}</div>
                  )}
                </TableCell>
                <TableCell>
                  {inv.total.toFixed(2)} {inv.currency}
                </TableCell>
                <TableCell>
                  {statusBadge(inv.status)}
                  {inv.prepayment && (
                    <div className={`mt-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${inv.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                      title={inv.status === "paid" ? "Vorauskasse eingegangen – Lieferung freigegeben" : "Vorauskasse: Lieferschein gesperrt bis Zahlungseingang"} data-testid={`invoice-prepayment-${inv._id}`} data-paid={inv.status === "paid"}>
                      <Banknote className="h-3 w-3" />{inv.status === "paid" ? "Vorauskasse bezahlt" : "Vorauskasse offen"}
                    </div>
                  )}
                  {overdueDays(inv) > 0 && !dunWarn?.due?.[inv._id] && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 whitespace-nowrap" title={`Fällig am ${formatDate(inv.due_date)}`} data-testid={`invoice-overdue-${inv._id}`}>
                      <AlertTriangle className="h-3 w-3" />{overdueDays(inv)} Tage überfällig
                      <button className="ml-1 underline hover:text-rose-950" onClick={(e) => sendDunning(e, inv)} data-testid={`invoice-overdue-dun-${inv._id}`}>Jetzt mahnen</button>
                    </div>
                  )}
                  {dunWarn?.due?.[inv._id] && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 whitespace-nowrap" title={`${dunWarn.due[inv._id].days_overdue} Tage überfällig`} data-testid={`invoice-dunning-due-${inv._id}`}>
                      <AlertTriangle className="h-3 w-3" />{overdueDays(inv) || dunWarn.due[inv._id].days_overdue} Tage überfällig · Mahnung Stufe {dunWarn.due[inv._id].next_level} fällig
                      <button className="ml-1 underline hover:text-rose-950" onClick={(e) => sendDunning(e, inv, dunWarn.due[inv._id].next_level)} data-testid={`invoice-dunning-send-${inv._id}`}>Jetzt mahnen</button>
                    </div>
                  )}
                  {dunWarn?.customers?.[inv.customer_id] && (
                    <div className="mt-0.5 text-[10px] text-rose-700 whitespace-nowrap" title={`Höchste Stufe ${dunWarn.customers[inv.customer_id].max_level}, zuletzt ${dunWarn.customers[inv.customer_id].last_dunning_date || "–"}`} data-testid={`invoice-dunning-repeat-${inv._id}`}>
                      Kunde {dunWarn.customers[inv.customer_id].dunned_invoices}× gemahnt (12 Mt.)
                    </div>
                  )}
                  {inv.payout_info && (
                    <div
                      className="mt-0.5 text-[10px] text-emerald-700 whitespace-nowrap"
                      title={`Payrexx-Export: Transaktion ${inv.payout_info.transaction_id || ""} · Brutto ${inv.payout_info.gross} · Gebühr ${inv.payout_info.fee} · Netto ${inv.payout_info.net}`}
                      data-testid={`invoice-payout-info-${inv._id}`}
                    >
                      Bezahlt {formatDate(inv.payout_info.paid_date)}{inv.payout_info.paid_time ? ` ${inv.payout_info.paid_time.slice(0, 5)}` : ""}
                      {inv.payout_info.method ? ` · ${inv.payout_info.method}` : ""} via {({ sumup: "SumUp", twint: "TWINT" })[inv.payout_info.provider] || "Payrexx"}
                      <span className="text-slate-500"> · Auszahlung {formatDate(inv.payout_info.payout_date)} ({inv.payout_info.reference}) · Gebühr CHF {Number(inv.payout_info.fee || 0).toFixed(2)}</span>
                      {inv.payout_info.amount_ok === false && <span className="text-amber-700"> · Betrag abweichend!</span>}
                    </div>
                  )}
                  {inv.paid_via === "camt054" && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]"
                      data-testid={`invoice-paid-via-camt054-${inv._id}`}
                      title="Auto-gebucht via PostFinance camt.054 (QR-Referenz erkannt)"
                    >
                      QR-Auto-Match
                    </Badge>
                  )}
                  {inv.lsv_collection_batch_id && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-sky-300 bg-sky-50 text-sky-800 text-[10px] cursor-pointer hover:bg-sky-100"
                      data-testid={`invoice-lsv-initiated-${inv._id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailBatchId(inv.lsv_collection_batch_id);
                      }}
                      title="Klicken für LSV-Batch-Details"
                    >
                      LSV initiiert
                    </Badge>
                  )}
                  {inv.paid_via === "lsv" && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]"
                      data-testid={`invoice-paid-via-lsv-${inv._id}`}
                      title="Auto-gebucht via PostFinance LSV pain.002 (Lastschrift erfolgreich eingezogen)"
                    >
                      LSV bezahlt
                    </Badge>
                  )}
                  {inv.lsv_rejected_reason && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-rose-300 bg-rose-50 text-rose-800 text-[10px]"
                      data-testid={`invoice-lsv-rejected-${inv._id}`}
                      title={inv.lsv_rejected_reason}
                    >
                      LSV abgelehnt
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPdf(inv)}
                    disabled={downloadingId === inv._id}
                    data-testid={`invoice-pdf-button-${inv._id}`}
                    title="PDF herunterladen"
                  >
                    {downloadingId === inv._id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  {isEuCountry(customerById[inv.customer_id]?.country) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-sky-300 text-sky-800 hover:bg-sky-50"
                      onClick={() => handleDownloadPdf(inv, true)}
                      disabled={downloadingId === inv._id}
                      data-testid={`invoice-facturx-button-${inv._id}`}
                      title="Factur-X / ZUGFeRD: PDF mit eingebettetem E-Rechnungs-XML (EU-Kunde)"
                    >
                      <FileCode2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEmailFor(inv)}
                    data-testid={`invoice-email-button-${inv._id}`}
                    title="Per E-Mail versenden"
                  >
                    <Mail className="h-3.5 w-3.5" />
                  </Button>
                  {ebillEnabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendEbill(inv)}
                      title="Rechnung elektronisch via SIX eBill direkt ins E-Banking des Kunden senden"
                      disabled={ebillSendingId === inv._id}
                      data-testid={`invoice-ebill-button-${inv._id}`}
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      {ebillSendingId === inv._id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <FileSignature className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  {mftpfEnabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendMftpf(inv)}
                      title="Zahlungsauftrag (pain.001) elektronisch an PostFinance senden"
                      disabled={mftpfSendingId === inv._id}
                      data-testid={`invoice-mftpf-button-${inv._id}`}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    >
                      {mftpfSendingId === inv._id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ServerCog className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePostInvoice(inv)}
                    data-testid={`invoice-post-button-${inv._id}`}
                  >
                    Buchen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPaymentForInvoice(inv)}
                    data-testid={`invoice-payment-button-${inv._id}`}
                    title="Zahlungseingang manuell erfassen (z.B. Barzahlung), falls er nicht über den Bank-Abgleich kommt"
                  >
                    Zahlung
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    onClick={() => deleteInvoice(inv)}
                    data-testid={`invoice-delete-button-${inv._id}`}
                    title="Rechnung löschen"
                  >
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={openPaymentDialog} onOpenChange={setOpenPaymentDialog}>
        <DialogContent data-testid="invoice-payment-dialog">
          <DialogHeader>
            <DialogTitle>Zahlung erfassen</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={handleRegisterPayment}
            data-testid="invoice-payment-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="payment_date">Zahlungsdatum</Label>
                <SwissDateField
                  id="payment_date"
                  
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  required
                  data-testid="invoice-payment-date-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="payment_amount">Betrag</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  data-testid="invoice-payment-amount-input"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="bank_account">Bankkonto (ID)</Label>
                <Input
                  id="bank_account"
                  value={paymentForm.bank_account_id}
                  onChange={(e) => setPaymentForm({ ...paymentForm, bank_account_id: e.target.value })}
                  data-testid="invoice-payment-bank-account-input"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="payment_description">Beschreibung</Label>
                <Input
                  id="payment_description"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                  data-testid="invoice-payment-description-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpenPaymentDialog(false)}
                data-testid="invoice-payment-cancel-button"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="rounded-full bg-slate-900 text-slate-50 px-5"
                data-testid="invoice-payment-submit-button"
              >
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={emailDialog.open}
        onOpenChange={(v) => setEmailDialog((prev) => ({ ...prev, open: v }))}
      >
        <DialogContent data-testid="invoice-email-dialog">
          <DialogHeader>
            <DialogTitle>Rechnung per E-Mail versenden</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={handleSendEmail} data-testid="invoice-email-form">
            <p className="text-xs text-slate-500">
              Die Rechnung wird als PDF-Anhang (inkl. Swiss QR-Zahlteil) über Ihre SMTP-Einstellungen
              versendet. Leer lassen = E-Mail des Kunden verwenden.
            </p>
            <div className="space-y-1">
              <Label htmlFor="email-recipient">Empfänger (optional)</Label>
              <Input
                id="email-recipient"
                type="email"
                placeholder="kunde@example.ch"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                data-testid="invoice-email-recipient-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email-subject">Betreff (optional)</Label>
              <Input
                id="email-subject"
                placeholder={`Rechnung ${emailDialog.invoice?.invoice_number || ""}`}
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="invoice-email-subject-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email-body">Nachricht (optional)</Label>
              <textarea
                id="email-body"
                className="flex min-h-[100px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400"
                placeholder="Sehr geehrte Damen und Herren, …"
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                data-testid="invoice-email-body-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEmailDialog({ open: false, invoice: null })}
                data-testid="invoice-email-cancel-button"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={emailSending}
                className="rounded-full bg-slate-900 text-slate-50 px-5"
                data-testid="invoice-email-send-button"
              >
                {emailSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Sende …
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-1.5" />
                    Senden
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <LsvCollectionWizard
        open={lsvWizardOpen}
        onClose={() => setLsvWizardOpen(false)}
        companyId={companyId}
        selectedInvoices={invoices.filter((i) => selectedInvoiceIds.includes(i._id))}
        onSuccess={async () => {
          setSelectedInvoiceIds([]);
          await loadInvoices();
        }}
      />

      <BatchDetailModal
        open={!!detailBatchId}
        onClose={() => setDetailBatchId(null)}
        companyId={companyId}
        batchId={detailBatchId}
        batchType="lsv"
      />
    </div>
  );
}
